'use client';
import {
  RelatedPostsWrapper,
  ShareContainer,
  ShareContainer2,
  ShareWrapper,
  SocialWrapper
} from "./components/styles";
import { Block, Box, MainContainerBlog, Image } from "../../styles/basicStyles";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "next-i18next";
import { BlogPost } from "../../models/blog";
import { colors } from "../../styles/colors";
import { useRouter } from "next/router";
import Typography from "../../components/typography";
import Article from "./components/article";
import Banner from "./components/banner";
import Blocks from "./components/blocks";
import dayjs from "dayjs";
import Icon from "../../components/icon";
import Metatags from "../../components/metatags";
import ScrollLoader from "../../components/views/scrollLoader";

interface Props {
  data: BlogPost;
}

const BlogDetail: FC<Props> = ({ data }) => {
  const { t, i18n: { language } } = useTranslation();
  const [scrollTop, setScrollTop] = useState<number>(0);
  const [mobile, _setMobile] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const AuthorRef = useRef<any>(null);
  const router = useRouter();

  const isMobileRef = useRef(mobile);
  const setMobile = (data: boolean) => {
    isMobileRef.current = data;
    _setMobile(data);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // We had to add a loader when clicking in the related posts because it would show the old one and then jump to the new post
  useEffect(() => {
    setLoading(false);
  }, [data.url, router.query.slug]);

  const handleScroll = () => {
    if(document.documentElement.scrollTop < AuthorRef?.current?.offsetTop - 510) setScrollTop(document.documentElement.scrollTop);
  };

  const handleResize = () => {
    if(window.innerWidth < 992) {
      if(!isMobileRef.current) setMobile(true);
    } 
    else {
      if(isMobileRef.current) setMobile(false);
    }
  };

  if(loading) return <Box fAlign="center" fJustify='center' h='100vh'><ScrollLoader /></Box>;

  return (
    <>
      <Metatags
        title={data.meta_title && data.meta_title[language]}
        description={data.meta_description && data.meta_description[language]}
        keywords={data.meta_keywords && data.meta_keywords[language]}
        image={data?.image_detail?.url}
      />
      <MainContainerBlog>
        <Box>
          <Box mt={{ md: 3.125, xxs: 2 }} mb={5.625} fDirection="column">
            <Banner
              title={data?.title && data.title[language]}
              date={dayjs(data?.date).format("D MMM YYYY")}
              category={data?.category?.name && data?.category.name[language]}
              image={mobile ? data?.image_detail_mobile?.url : data?.image_detail?.url}
              description={data?.description && data?.description[language]}
            />
            {
              data?.blocks?.map((block, index) =>
                <Blocks
                  key={index}
                  block={block}
                />
              )
            }
            <Block
              h={0.094}
              bgColor={colors.softPeach}
              w="100%"
              mt={2.5}
              mb={0.813}
            />
            <ShareContainer
              ref={AuthorRef}
              fJustify="space-between"
              fDirection={{ sm: "row", xxs: "column" }}
            >
              <Box fAlign="center">
                <Block>
                  <Image
                    loading='lazy'
                    src={data?.author?.image?.url}
                    style={{ with: "5rem", height: "5rem" }}
                    bRadius="50%"
                    alt='author image'
                  />
                </Block>
                <Box fDirection="column" pl={0.938}>
                  <Typography
                    fSize={{ sm: 1.25, xxs: 1 }}
                    lHeight={{ sm: 1.875, xxs: 1.625 }}
                    fWeight={700}
                    style={{ color: colors.black }}
                  >
                    {data?.author?.name}
                  </Typography>
                  <Typography
                    fSize={{ md: 1, xxs: 0.75 }}
                    lHeight={{ md: 1.313, xxs: 1 }}
                    fWeight={400}
                    style={{ color: colors.black }}
                  >
                    {data?.author?.job}
                  </Typography>
                </Box>
              </Box>
              <Box fJustify="flex-end" fAlign="center">
                <Typography
                  fSize={1.125}
                  lHeight={1.375}
                  fWeight={400}
                  style={{ color: colors.black }}
                >
                  {t("SHARE_SINGLE")}
                </Typography>
                <a
                  href={`http://www.facebook.com/sharer.php?u=${process.env.NEXT_PUBLIC_URL}blog/${data?.url}`}
                  target="_blank"
                  rel="nofollow"
                  aria-label="Facebook share"
                >
                  <SocialWrapper>
                    <Icon
                      icon="social_facebook"
                      color={colors.white}
                      size={1.175}
                    />
                  </SocialWrapper>
                </a>
                <a
                  href={`https://www.linkedin.com/shareArticle?mini=true&url=${process.env.NEXT_PUBLIC_URL}blog/${data?.url}&source=Codepoint`}
                  target="_blank"
                  rel="nofollow"
                  aria-label="LinkedIn share"
                >
                  <SocialWrapper>
                    <Icon
                      icon="social_linkedin"
                      color={colors.white}
                      size={1.175}
                    />
                  </SocialWrapper>
                </a>
                <a
                  href={`https://x.com/intent/tweet?text=${process.env.NEXT_PUBLIC_URL}blog/${data?.url}`}
                  target="_blank"
                  rel="nofollow"
                  aria-label="X share"
                >
                  <SocialWrapper>
                    <Icon
                      icon="social_x"
                      color={colors.white}
                      size={1.175}
                    />
                  </SocialWrapper>
                </a>
              </Box>
            </ShareContainer>
            {
              data?.featuredPosts && data?.featuredPosts?.length > 0 &&
              <Box mt={{ md: 9.375, xxs: 6.875 }} fDirection="column">
                <Typography
                  fSize={1.875}
                  lHeight={2.625}
                  fWeight={700}
                  style={{ color: colors.black }}
                >
                  {t("MORE_INTERESTING_ARTICLES")}
                </Typography>
                <RelatedPostsWrapper
                  margin={{ xs: "0 -1.156rem", xxs: 0 }}
                  pt={2.625}
                >
                  {
                    data.featuredPosts.map((e, index) =>
                      <Article
                        key={index}
                        image={e.image.url}
                        title={e.title && e.title[language]}
                        category={e.category?.name && e.category.name[language]}
                        url={e.url}
                        setLoading={setLoading}
                      />
                    )
                  }
                </RelatedPostsWrapper>
              </Box>
            }
          </Box>
          <ShareContainer2
            display={{ md: "flex", xxs: "none" }}
            scrollTop={scrollTop}
          >
            <Typography
              fSize={0.875}
              lHeight={1.063}
              fWeight={400}
              style={{ color: colors.black }}
            >
              {t("SHARE_SINGLE")}
            </Typography>
            <Box fAlign="center" fJustify="center" fDirection="column">
              <a
                href={`http://www.facebook.com/sharer.php?u=${process.env.NEXT_PUBLIC_URL}blog/${data?.url}`}
                target="_blank"
                rel="nofollow"
                aria-label="Facebook share"
              >
                <ShareWrapper>
                  <Icon
                    icon="social_facebook"
                    color={colors.white}
                    size={1.175}
                  />
                </ShareWrapper>
              </a>
              <a
                href={`https://www.linkedin.com/shareArticle?mini=true&url=${process.env.NEXT_PUBLIC_URL}blog/${data?.url}&source=Codepoint`}
                target="_blank"
                rel="nofollow"
                aria-label="LinkedIn share"
              >
                <ShareWrapper>
                  <Icon
                    icon="social_linkedin"
                    color={colors.white}
                    size={1.175}
                  />
                </ShareWrapper>
              </a>
              <a
                href={`https://x.com/intent/tweet?text=${process.env.NEXT_PUBLIC_URL}blog/${data?.url}`}
                target="_blank"
                rel="nofollow"
                aria-label="X share"
              >
                <ShareWrapper>
                  <Icon icon="social_x" color={colors.white} size={1.175} />
                </ShareWrapper>
              </a>
            </Box>
          </ShareContainer2>
        </Box>
      </MainContainerBlog>
    </>
  );
};

export default BlogDetail;
